import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Bell,
  UserPlus,
  Heart,
  MessageCircle,
  X,
  Check,
  Trash,
  MailOpen,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

function toInt(x) {
  const v = Number(x);
  return Number.isFinite(v) ? v : 0;
}

function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [showDropdown, setShowDropdown] = useState(false);

  // Notifications (DB table)
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);

  // Friend requests (separate system)
  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);

  const [loading, setLoading] = useState(false);

  const dropdownRef = useRef(null);

  const close = useCallback(() => setShowDropdown(false), []);

  const pendingReqCount = pendingRequests.length;

  const totalBadgeCount = useMemo(
    () => Math.max(0, unreadNotifCount) + Math.max(0, pendingReqCount),
    [unreadNotifCount, pendingReqCount]
  );

  // Reset on logout
  useEffect(() => {
    if (!user) {
      setShowDropdown(false);
      setNotifications([]);
      setUnreadNotifCount(0);
      setPendingRequests([]);
      setLoading(false);
      setPendingLoading(false);
    }
  }, [user]);

  // Outside click closes
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) close();
    }
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showDropdown, close]);

  // ESC closes
  useEffect(() => {
    if (!showDropdown) return;
    const onKeyDown = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showDropdown, close]);

  // Lock scroll when open (mobile)
  useEffect(() => {
    if (!showDropdown) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showDropdown]);

  // Poll counts
  useEffect(() => {
    if (!user) return;

    fetchUnreadNotifCount();
    fetchPendingRequests();

    const interval = setInterval(() => {
      fetchUnreadNotifCount();
      fetchPendingRequests();
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // When opening dropdown, refresh lists
  useEffect(() => {
    if (!user) return;
    if (showDropdown) {
      fetchNotifications();
      fetchPendingRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDropdown, user]);

  const fetchUnreadNotifCount = async () => {
    if (!user) return;
    try {
      const res = await fetch("/api/notifications/unread/count", { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setUnreadNotifCount(toInt(data.count));
      }
    } catch (error) {
      console.error("Error fetching unread notif count:", error);
    }
  };

  const fetchNotifications = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const res = await fetch("/api/notifications?limit=20", { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  // Friend requests: uses the same endpoint your ProfileHub uses
  const fetchPendingRequests = async () => {
    if (!user) return;
    try {
      setPendingLoading(true);
      const res = await fetch("/api/friends/requests", { credentials: "include" });
      if (!res.ok) return;

      const data = await res.json().catch(() => null);

      // Support multiple backend shapes:
      // - { requests: [...] }
      // - { pendingRequests: [...] }
      // - [ ... ]
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.requests)
          ? data.requests
          : Array.isArray(data?.pendingRequests)
            ? data.pendingRequests
            : [];

      setPendingRequests(list);
    } catch (error) {
      console.error("Error fetching pending friend requests:", error);
    } finally {
      setPendingLoading(false);
    }
  };

  const handleBellClick = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setShowDropdown((v) => !v);
  };

  const handleNotificationClick = async (notification) => {
    if (!user) return;

    if (!notification.is_read) {
      try {
        await fetch(`/api/notifications/${notification.notification_id}/read`, {
          method: "PUT",
          credentials: "include",
        });

        setNotifications((prev) =>
          prev.map((n) =>
            n.notification_id === notification.notification_id ? { ...n, is_read: true } : n
          )
        );
        setUnreadNotifCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    }

    if (notification.link) {
      close();
      navigate(notification.link);
    }
  };

  const handleMarkAsRead = async (e, notificationId) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) return;

    try {
      await fetch(`/api/notifications/${notificationId}/read`, {
        method: "PUT",
        credentials: "include",
      });

      setNotifications((prev) =>
        prev.map((n) => (n.notification_id === notificationId ? { ...n, is_read: true } : n))
      );
      setUnreadNotifCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleMarkAllRead = async () => {
    if (!user) return;
    try {
      await fetch("/api/notifications/mark-all-read", {
        method: "PUT",
        credentials: "include",
      });

      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadNotifCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleDeleteNotification = async (e, notificationId) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) return;

    const wasUnread = notifications.some(
      (n) => n.notification_id === notificationId && !n.is_read
    );

    try {
      await fetch(`/api/notifications/${notificationId}`, {
        method: "DELETE",
        credentials: "include",
      });

      setNotifications((prev) => prev.filter((n) => n.notification_id !== notificationId));
      if (wasUnread) setUnreadNotifCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  // Accept / Reject inside dropdown
  const handleAcceptRequest = async (e, friendshipId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;

    try {
      await fetch(`/api/friends/accept/${friendshipId}`, {
        method: "PUT",
        credentials: "include",
      });

      // remove from list immediately
      setPendingRequests((prev) => prev.filter((r) => r.friendship_id !== friendshipId));
    } catch (error) {
      console.error("Error accepting friend request:", error);
    }
  };

  const handleRejectRequest = async (e, friendshipId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;

    try {
      await fetch(`/api/friends/reject/${friendshipId}`, {
        method: "PUT",
        credentials: "include",
      });

      setPendingRequests((prev) => prev.filter((r) => r.friendship_id !== friendshipId));
    } catch (error) {
      console.error("Error rejecting friend request:", error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "friend_request":
        return <UserPlus size={18} className="text-cyan-300" />;
      case "like":
        return <Heart size={18} className="text-pink-300" />;
      case "comment":
      case "reply":
        return <MessageCircle size={18} className="text-violet-300" />;
      default:
        return <Bell size={18} className="text-white/60" />;
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffInSeconds = Math.floor((now - past) / 1000);

    if (diffInSeconds < 60) return "just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return past.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleBellClick}
        className="
          relative inline-flex items-center justify-center p-2
          text-white/70 hover:text-white/90
          transition-colors
          rounded-full
          focus:outline-none focus:ring-2 focus:ring-emerald-300/25
        "
        aria-label="Notifications"
        aria-expanded={showDropdown}
      >
        <Bell size={22} className="text-current opacity-90" />

        {user && totalBadgeCount > 0 && (
          <span
            className="
              absolute -top-0.5 -right-0.5
              min-w-[18px] h-[18px] px-1
              bg-red-500/90 text-white/95
              text-[10px] font-bold
              rounded-full
              flex items-center justify-center
              shadow-[0_0_0_2px_rgba(10,10,15,0.9)]
            "
          >
            {totalBadgeCount > 99 ? "99+" : totalBadgeCount}
          </span>
        )}
      </button>

      {user && showDropdown && (
        <>
          <button
            aria-label="Close notifications overlay"
            onClick={close}
            className="
              fixed inset-0 z-40
              bg-black/55
              backdrop-blur-[2px]
              md:hidden
            "
          />

          <div
            className="
              z-50
              md:absolute md:right-0 md:mt-2
              fixed md:static
              left-1/2 md:left-auto
              -translate-x-1/2 md:translate-x-0
              top-[10vh] md:top-auto
              w-[calc(100vw-1.25rem)] md:w-[24rem]
              max-w-[28rem]
              max-h-[80vh] md:max-h-[600px]
              rounded-2xl
              border border-white/12
              bg-[#0a0a0f]/95
              shadow-2xl
              overflow-hidden
              backdrop-blur-2xl
              ring-1 ring-white/10
            "
          >
            <div className="sticky top-0 z-10 border-b border-white/10 bg-[#0a0a0f]/95 backdrop-blur-2xl">
              <div className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0">
                  <h3 className="text-[15px] font-semibold text-white tracking-wide">
                    Notifications
                  </h3>
                  <p className="text-[11px] text-white/45 mt-0.5">
                    {unreadNotifCount > 0 ? `${unreadNotifCount} unread` : "All caught up"}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {unreadNotifCount > 0 && (
                    <button
                      onClick={handleMarkAllRead}
                      className="
                        inline-flex items-center gap-2
                        h-9 px-3 rounded-full
                        border border-white/10
                        bg-white/[0.03]
                        text-[12px] font-semibold
                        text-emerald-200/90
                        hover:bg-white/[0.06]
                        hover:border-white/20
                        transition
                        focus:outline-none focus:ring-2 focus:ring-emerald-300/25
                      "
                    >
                      <Check size={16} className="text-emerald-200/90" />
                      Mark all
                    </button>
                  )}

                  <button
                    onClick={close}
                    className="
                      inline-flex items-center justify-center
                      h-9 w-9 rounded-full
                      border border-white/10
                      bg-white/[0.03]
                      hover:bg-white/[0.06]
                      hover:border-white/20
                      transition
                      focus:outline-none focus:ring-2 focus:ring-emerald-300/25
                    "
                    aria-label="Close notifications"
                  >
                    <X size={16} className="text-white/70" />
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-y-auto max-h-[calc(80vh-64px)] md:max-h-[500px] scrollbar-hide">
              {/* Friend Requests section (shows even if notifications is empty) */}
              {pendingLoading ? (
                <div className="px-4 py-3 text-white/50 text-sm">Loading friend requests...</div>
              ) : pendingRequests.length > 0 ? (
                <div className="px-4 py-3 border-b border-white/[0.06]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UserPlus size={16} className="text-cyan-300" />
                      <div className="text-[12px] font-semibold text-white/85">
                        Friend Requests
                      </div>
                    </div>
                    <div className="text-[11px] text-white/45">
                      {pendingRequests.length} pending
                    </div>
                  </div>

                  <div className="mt-3 space-y-2">
                    {pendingRequests.map((r) => (
                      <div
                        key={r.friendship_id}
                        className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {r.profile_picture ? (
                            <img
                              src={r.profile_picture}
                              alt={r.username || "User"}
                              className="w-10 h-10 rounded-xl object-cover border border-white/15 shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-xl border border-white/12 bg-white/[0.035] flex items-center justify-center shrink-0">
                              <UserPlus size={18} className="text-cyan-300" />
                            </div>
                          )}

                          <div className="min-w-0">
                            <div className="text-[13px] text-white/90 font-semibold truncate">
                              {r.username || "Someone"}
                            </div>
                            <div className="text-[11px] text-white/45">
                              sent you a friend request
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={(e) => handleAcceptRequest(e, r.friendship_id)}
                            className="
                              h-8 px-3 rounded-lg
                              border border-cyan-400/30
                              bg-cyan-500/15
                              text-cyan-200/90
                              hover:bg-cyan-500/25
                              transition
                              text-[12px] font-semibold
                            "
                          >
                            Accept
                          </button>
                          <button
                            onClick={(e) => handleRejectRequest(e, r.friendship_id)}
                            className="
                              h-8 px-3 rounded-lg
                              border border-white/10
                              bg-white/[0.03]
                              text-white/70
                              hover:bg-white/[0.07]
                              transition
                              text-[12px]
                            "
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Notifications list */}
              {loading ? (
                <div className="p-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-300/90" />
                  <p className="text-white/50 mt-3 text-sm">Loading...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-10 text-center">
                  <div className="mx-auto mb-4 h-14 w-14 rounded-2xl border border-white/10 bg-white/[0.03] flex items-center justify-center">
                    <Bell size={28} className="text-white/25" />
                  </div>
                  <p className="text-white/70 text-sm font-semibold">No notifications yet</p>
                  <p className="text-white/40 text-xs mt-1">
                    Activity will show up here as you interact.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-white/[0.06]">
                  {notifications.map((notification) => {
                    const unread = !notification.is_read;

                    return (
                      <div
                        key={notification.notification_id}
                        onClick={() => handleNotificationClick(notification)}
                        className={[
                          "group cursor-pointer transition-colors",
                          "px-4 py-3",
                          "hover:bg-white/[0.045]",
                          unread ? "bg-emerald-500/[0.06]" : "bg-transparent",
                        ].join(" ")}
                      >
                        <div className="flex items-start gap-3">
                          {notification.source_profile_picture ? (
                            <img
                              src={notification.source_profile_picture}
                              alt={notification.source_username || "User"}
                              className="w-10 h-10 rounded-xl object-cover object-center border border-white/15 shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-xl border border-white/12 bg-white/[0.035] flex items-center justify-center shrink-0">
                              {getNotificationIcon(notification.type)}
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] text-white/90 leading-snug">
                              {notification.message}
                            </p>
                            <div className="mt-1 flex items-center gap-2">
                              <p className="text-[11px] text-white/45">
                                {getTimeAgo(notification.created_at)}
                              </p>
                              {unread && (
                                <span className="text-[10px] text-emerald-200/80">• New</span>
                              )}
                            </div>
                          </div>

                          <div
                            className="flex items-center gap-2 shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {unread ? (
                              <>
                                <button
                                  onClick={(e) =>
                                    handleMarkAsRead(e, notification.notification_id)
                                  }
                                  className="
                                    p-2 rounded-xl
                                    border border-white/10
                                    bg-white/[0.02]
                                    hover:bg-white/[0.06]
                                    hover:border-white/20
                                    transition
                                    opacity-100 md:opacity-0 md:group-hover:opacity-100
                                    focus:outline-none focus:ring-2 focus:ring-emerald-300/25
                                  "
                                  aria-label="Mark as read"
                                  title="Mark as read"
                                >
                                  <MailOpen size={14} className="text-emerald-300/80" />
                                </button>

                                <button
                                  onClick={(e) =>
                                    handleDeleteNotification(e, notification.notification_id)
                                  }
                                  className="
                                    p-2 rounded-xl
                                    border border-white/10
                                    bg-white/[0.02]
                                    hover:bg-red-500/10
                                    hover:border-red-500/30
                                    transition
                                    opacity-100 md:opacity-0 md:group-hover:opacity-100
                                    focus:outline-none focus:ring-2 focus:ring-red-300/25
                                  "
                                  aria-label="Delete notification"
                                  title="Delete notification"
                                >
                                  <Trash size={14} className="text-red-400/80" />
                                </button>

                                <div className="w-2 h-2 rounded-full bg-emerald-300 shadow-[0_0_12px_rgba(16,185,129,0.35)]" />
                              </>
                            ) : (
                              <button
                                onClick={(e) =>
                                  handleDeleteNotification(e, notification.notification_id)
                                }
                                className="
                                  p-2 rounded-xl
                                  border border-white/10
                                  bg-white/[0.02]
                                  hover:bg-red-500/10
                                  hover:border-red-500/30
                                  transition
                                  opacity-0 md:group-hover:opacity-100
                                  focus:outline-none focus:ring-2 focus:ring-red-300/25
                                "
                                aria-label="Delete notification"
                                title="Delete notification"
                              >
                                <Trash size={14} className="text-red-400/80" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="md:hidden border-t border-white/10 bg-[#0a0a0f]/95 backdrop-blur-2xl px-4 py-3">
              <p className="text-[11px] text-white/40">
                Tip: tap outside to close.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default NotificationBell;

